/**
 * Adaptive microphone level control — the "it adjusts itself" half of the
 * smart noise suppression (RNNoise removes the noise, this decides how loud
 * the result should be and when to duck it).
 *
 * Why a hand-written controller instead of the browser's own AGC: the browser
 * one is a black box that reacts to *any* energy, so it pumps up the room hiss
 * during pauses and then clips the first word. Here we track the noise floor
 * separately from the speech level, so gain is only ever derived from actual
 * speech and pauses are ducked instead of amplified.
 *
 * Pure and frame-rate independent on purpose: every step takes the measured
 * level plus the elapsed time, so the same code runs from a 25 ms polling loop
 * and from a unit test that feeds it a synthetic recording.
 */

export interface AdaptiveGainOptions {
  /** Level speech is normalised to, dBFS. -18 is the usual broadcast target. */
  targetDb: number;
  /** How much we are allowed to amplify a quiet microphone. */
  maxGainDb: number;
  /** How much we are allowed to attenuate a hot microphone. */
  minGainDb: number;
  /**
   * How much quieter than the room itself a pause should be.
   *
   * Applied on top of whatever the AGC is holding, so the result is measured
   * against the microphone, not against the amplified signal: with 18 dB of
   * gain a plain -18 dB duck cancels out to exactly nothing, which is how the
   * stand caught this — the hiss came out at the level it went in.
   */
  duckDb: number;
  /** How far above the noise floor counts as speech. */
  openMarginDb: number;
  /** Hysteresis: speech keeps the gate open until it drops this much lower. */
  hysteresisDb: number;
  /** Gate stays open this long after the last speech-level frame. */
  holdMs: number;
  /** Ceiling on how fast the AGC gain may move, dB per second. */
  gainSlewDbPerSec: number;
  /**
   * Fixed speech threshold in dBFS, or null to derive it from the room.
   *
   * Manual mode exists for the cases automatic tracking cannot win: a room
   * whose noise is itself speech (an open office, a TV in the background),
   * where "louder than the room" is the wrong question to ask.
   */
  manualThresholdDb: number | null;
}

export const DEFAULT_ADAPTIVE_GAIN: AdaptiveGainOptions = {
  targetDb: -18,
  maxGainDb: 18,
  minGainDb: -12,
  duckDb: -18,
  openMarginDb: 9,
  hysteresisDb: 4,
  holdMs: 300,
  gainSlewDbPerSec: 6,
  manualThresholdDb: null,
};

export interface AdaptiveGainState {
  /** Gain the AGC wants, dB. Changes slowly — it is a level, not a gate. */
  gainDb: number;
  /** Extra attenuation for this moment: 0 while speaking, `duckDb` in pauses. */
  duckDb: number;
  /** Whether the controller currently believes there is speech. */
  speaking: boolean;
  /** Estimated background noise level, dBFS. */
  noiseFloorDb: number;
  /** Level of the last frame, dBFS. */
  levelDb: number;
  /** Level at which the gate opens, dBFS. */
  thresholdDb: number;
}

/** Anything quieter than this is treated as digital silence. */
export const SILENCE_DB = -100;

/** Linear amplitude → dBFS, floored at SILENCE_DB (log(0) is not a number). */
export function amplitudeToDb(amplitude: number): number {
  if (!(amplitude > 0)) return SILENCE_DB;
  return Math.max(SILENCE_DB, 20 * Math.log10(amplitude));
}

/** dB → linear gain factor. */
export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

/** RMS of a block of samples, as linear amplitude. */
export function rms(samples: ArrayLike<number>): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return samples.length ? Math.sqrt(sum / samples.length) : 0;
}

/** One-pole smoothing coefficient for a given time constant. */
function coefficient(dtMs: number, tauMs: number): number {
  if (tauMs <= 0) return 1;
  return 1 - Math.exp(-dtMs / tauMs);
}

// Noise floor tracking. Falling fast and rising slowly is what makes the floor
// follow the room rather than the voice: a pause immediately pulls it down, a
// shout barely moves it.
const FLOOR_FALL_MS = 200;
const FLOOR_RISE_MS = 800;
// While the gate is open the floor still creeps up, just thirty times slower.
// Freezing it outright looks tidier and is a trap: if the floor is ever left
// too low — the graph fed us silence at startup, someone joined mid-sentence —
// then everything counts as speech, the gate never closes, and a frozen floor
// has no way back. A sentence moves it by a fraction of a dB; a stuck estimate
// still corrects itself within a minute.
const FLOOR_RISE_SPEAKING_MS = 30000;
// Below this there is no signal to reason about: an audio graph that has not
// started yet reads as digital silence, and "the room is this quiet" is the
// one conclusion that breaks everything downstream.
const NO_SIGNAL_DB = SILENCE_DB + 5;
// The floor starts at the top and falls onto the room. Seeding it from the
// first frame instead looks obvious and is wrong twice over: the graph fades
// in, so the first frames are neither silence nor the real room, and a floor
// seeded too low leaves the gate open on the hiss (caught on the stand — the
// hiss came out 18 dB louder than it went in).
const FLOOR_START_DB = 0;
// Decisions are held back until the estimate has had time to fall. Long enough
// to settle, short enough that nobody notices the microphone "waking up".
const WARMUP_MS = 400;
// Speech level tracking: quick to notice someone got louder, slow to forget,
// so a single quiet syllable does not send the gain climbing.
const SPEECH_RISE_MS = 150;
const SPEECH_FALL_MS = 2000;

export class AdaptiveGainController {
  options: AdaptiveGainOptions;

  #noiseFloorDb = FLOOR_START_DB;
  #speechLevelDb: number | null = null;
  #gainDb = 0;
  #speaking = false;
  #holdLeftMs = 0;
  /** Time the controller has seen actual signal, ms — see WARMUP_MS. */
  #signalMs = 0;

  constructor(options: Partial<AdaptiveGainOptions> = {}) {
    this.options = { ...DEFAULT_ADAPTIVE_GAIN, ...options };
  }

  /**
   * Change settings without dropping what has been learned about the room:
   * the slider is dragged while someone is talking, and rebuilding the
   * controller mid-sentence would re-run the warm-up and mute them.
   */
  setOptions(options: Partial<AdaptiveGainOptions>): void {
    this.options = { ...this.options, ...options };
  }

  /** Current AGC gain in dB — survives across pauses. */
  get gainDb(): number {
    return this.#gainDb;
  }

  get noiseFloorDb(): number {
    return this.#noiseFloorDb;
  }

  get speaking(): boolean {
    return this.#speaking;
  }

  /** Forget everything — used when the microphone or device changes. */
  reset(): void {
    this.#noiseFloorDb = FLOOR_START_DB;
    this.#speechLevelDb = null;
    this.#gainDb = 0;
    this.#speaking = false;
    this.#holdLeftMs = 0;
    this.#signalMs = 0;
  }

  /**
   * Feed one measurement.
   *
   * @param levelDb level of the last audio block, dBFS
   * @param dtMs time since the previous measurement
   */
  push(levelDb: number, dtMs: number): AdaptiveGainState {
    const o = this.options;
    const dt = Math.max(1, Math.min(1000, dtMs));

    const hasSignal = levelDb > NO_SIGNAL_DB;
    if (hasSignal) this.#signalMs += dt;
    const warm = this.#signalMs >= WARMUP_MS;

    if (!hasSignal) {
      // Nothing to learn from silence — and nothing to say into it either.
    } else if (levelDb < this.#noiseFloorDb) {
      this.#noiseFloorDb +=
        (levelDb - this.#noiseFloorDb) * coefficient(dt, FLOOR_FALL_MS);
    } else {
      this.#noiseFloorDb +=
        (levelDb - this.#noiseFloorDb) *
        coefficient(dt, this.#speaking ? FLOOR_RISE_SPEAKING_MS : FLOOR_RISE_MS);
    }
    this.#noiseFloorDb = Math.max(SILENCE_DB, this.#noiseFloorDb);

    if (!warm) {
      // Still learning the room: stay closed and leave the gain alone rather
      // than act on an estimate that is still falling.
      return {
        gainDb: this.#gainDb,
        duckDb: o.duckDb,
        speaking: false,
        noiseFloorDb: this.#noiseFloorDb,
        levelDb,
        thresholdDb:
          o.manualThresholdDb === null
            ? this.#noiseFloorDb + o.openMarginDb
            : o.manualThresholdDb,
      };
    }

    const openDb =
      o.manualThresholdDb === null
        ? this.#noiseFloorDb + o.openMarginDb
        : o.manualThresholdDb;
    const closeDb = openDb - o.hysteresisDb;

    if (this.#speaking) {
      if (levelDb > closeDb) {
        this.#holdLeftMs = o.holdMs;
      } else {
        this.#holdLeftMs -= dt;
        if (this.#holdLeftMs <= 0) this.#speaking = false;
      }
    } else if (hasSignal && levelDb > openDb) {
      this.#speaking = true;
      this.#holdLeftMs = o.holdMs;
    }

    if (this.#speaking && levelDb > closeDb) {
      if (this.#speechLevelDb === null) {
        this.#speechLevelDb = levelDb;
      } else {
        const tau =
          levelDb > this.#speechLevelDb ? SPEECH_RISE_MS : SPEECH_FALL_MS;
        this.#speechLevelDb +=
          (levelDb - this.#speechLevelDb) * coefficient(dt, tau);
      }

      // Gain is recomputed from speech only. In a pause it is simply held —
      // that is the whole difference from the browser AGC, which would spend
      // the pause amplifying the room.
      const wanted = clamp(
        o.targetDb - this.#speechLevelDb,
        o.minGainDb,
        o.maxGainDb,
      );
      const step = (o.gainSlewDbPerSec * dt) / 1000;
      this.#gainDb += clamp(wanted - this.#gainDb, -step, step);
    }

    return {
      gainDb: this.#gainDb,
      // Cancel the AGC first, then duck — a pause always lands `duckDb` below
      // the room, however loud the gain has grown.
      duckDb: this.#speaking
        ? 0
        : Math.max(-40, o.duckDb - Math.max(0, this.#gainDb)),
      speaking: this.#speaking,
      noiseFloorDb: this.#noiseFloorDb,
      levelDb,
      thresholdDb: openDb,
    };
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
